import React from 'react';
import { cn } from '../../../utils/cn';

interface FieldShellProps {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  id?: string;
  className?: string;
  children: React.ReactNode;
}

const FieldShell: React.FC<FieldShellProps> = ({
  label,
  hint,
  error,
  required,
  id,
  className,
  children,
}) => (
  <div className={cn('flex flex-col gap-1.5', className)}>
    {label && (
      <label
        htmlFor={id}
        className="text-[12px] font-medium text-[var(--rt-ink-700)] dark:text-zinc-300"
      >
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
    )}
    {children}
    {error ? (
      <span className="text-[11.5px] text-red-600 dark:text-red-400">
        {error}
      </span>
    ) : hint ? (
      <span className="text-[11.5px] text-[var(--rt-ink-500)] dark:text-zinc-400">
        {hint}
      </span>
    ) : null}
  </div>
);

const INPUT_BASE =
  'w-full h-10 px-3 rounded-[10px] text-[14px] bg-white border border-[rgba(15,23,21,.12)] text-[var(--rt-ink-900)] placeholder:text-[var(--rt-ink-400)] focus:outline-none focus:border-[var(--rt-brand-500)] focus:ring-2 focus:ring-[var(--rt-brand-500)]/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed dark:bg-[#12171400] dark:bg-opacity-0 dark:border-white/10 dark:text-white dark:placeholder:text-zinc-500 dark:focus:border-[var(--rt-brand-400)]';

export interface InputFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  fieldClassName?: string;
}

export const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      label,
      hint,
      error,
      required,
      className,
      fieldClassName,
      leading,
      trailing,
      id,
      ...rest
    },
    ref,
  ) => {
    const autoId = React.useId();
    const fieldId = id ?? autoId;
    return (
      <FieldShell
        label={label}
        hint={hint}
        error={error}
        required={required}
        id={fieldId}
        className={className}
      >
        <div className="relative">
          {leading && (
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[var(--rt-ink-400)] pointer-events-none">
              {leading}
            </span>
          )}
          <input
            ref={ref}
            id={fieldId}
            aria-invalid={error ? 'true' : undefined}
            required={required}
            {...rest}
            className={cn(
              INPUT_BASE,
              leading && 'pl-9',
              trailing && 'pr-9',
              error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
              fieldClassName,
            )}
          />
          {trailing && (
            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-[var(--rt-ink-400)]">
              {trailing}
            </span>
          )}
        </div>
      </FieldShell>
    );
  },
);
InputField.displayName = 'InputField';

export interface TextareaFieldProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  fieldClassName?: string;
}

export const TextareaField = React.forwardRef<
  HTMLTextAreaElement,
  TextareaFieldProps
>(
  (
    { label, hint, error, required, className, fieldClassName, id, ...rest },
    ref,
  ) => {
    const autoId = React.useId();
    const fieldId = id ?? autoId;
    return (
      <FieldShell
        label={label}
        hint={hint}
        error={error}
        required={required}
        id={fieldId}
        className={className}
      >
        <textarea
          ref={ref}
          id={fieldId}
          aria-invalid={error ? 'true' : undefined}
          required={required}
          {...rest}
          className={cn(
            INPUT_BASE,
            'h-auto min-h-[88px] py-2.5 resize-y',
            error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
            fieldClassName,
          )}
        />
      </FieldShell>
    );
  },
);
TextareaField.displayName = 'TextareaField';

export default InputField;
